import { memo, useState } from 'react'
import { flushSync } from 'react-dom'
import { useDraggable, useDroppable } from '@dnd-kit/core'
import { useTreeDnd } from '../dnd/TreeDnd'
import { DropBar, DropInside, tintClass } from './CardGrid'
import { displayTitle } from '@terrazzo/core'
import { useItem, useSubtreeTotal, useTab } from '../store/hooks'
import { store } from '../store/store'
import { actions } from '../store/actions'
import { startEditing } from '../store/ui'
import { Checklist } from './Checklist'
import { ephemeral } from './Row'
import { IconChevronRight, IconInbox, IconMore, IconPin, IconSparkle } from './Icons'
import { navigate, itemPath, openSheet } from '../lib/router'
import { useFileUrl } from '../lib/uploads'

const isEmptyCard = (id: string) => {
  const it = store.get(id)
  return !!it && !it.title.trim() && !(store.tree.childrenOf.get(id)?.length ?? 0) && !it.bodyText && !it.attachments.length
}

/** Remove a just-created card again if nothing was ever put in it. */
export function dropIfEmptyNewCard(id: string | null | undefined) {
  if (id && ephemeral.has(id) && isEmptyCard(id)) {
    ephemeral.delete(id)
    actions.purge(id)
  }
}

/**
 * A card's heading, editable like any heading row: tap it to rename in place, with › beside it
 * to open the card (as a row shows while you edit it). Enter starts the card's first item.
 */
export function CardTitle({ id, scope, title }: { id: string; scope: string; title: string }) {
  const [editing, setEditing] = useState(false)
  const [text, setText] = useState('')
  if (!editing)
    return (
      <button
        type="button"
        onClick={() => {
          setText(displayTitle(store.get(id)?.title ?? ''))
          setEditing(true)
        }}
        className="card-name min-w-0 flex-1 cursor-text truncate py-1.5 text-left"
        data-card-title={id}
      >
        {title}
      </button>
    )
  return (
    <>
      <input
        autoFocus
        value={text}
        aria-label="Card name"
        enterKeyHint="next"
        autoCapitalize="sentences"
        onPointerDown={(e) => e.stopPropagation()}
        onChange={(e) => {
          setText(e.target.value)
          actions.setTitle(id, e.target.value)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
            e.preventDefault()
            // Start a new first item (focus moves inside the keypress, so the keyboard stays up).
            const row = actions.addItem({ parentId: id, at: 'start', title: '', done: false })
            ephemeral.add(row)
            flushSync(() => setEditing(false))
            startEditing(scope, row, 'start')
          } else if (e.key === 'Escape') {
            e.preventDefault()
            e.currentTarget.blur()
          }
        }}
        onBlur={() => setEditing(false)}
        className="card-name min-w-0 flex-1 bg-transparent py-1.5 outline-none"
      />
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={() => navigate(itemPath(id))}
        aria-label="Open"
        className="flex h-8 w-8 flex-none items-center justify-center opacity-80 hover:opacity-100"
      >
        <span className="open-box">
          <IconChevronRight size={15} />
        </span>
      </button>
    </>
  )
}

/**
 * A card (SPEC §7.10). On phones it's a stretch of the ledger: edge to edge, no space around it,
 * starting with its bar (the name in mono capitals, solid ink or the card's color), which stays
 * under the tabs while its rows scroll by. On desktop it keeps edges. `reorderable` (a board):
 * other cards can be dragged onto this one to land above or below it — a bar shows which.
 */
export const Card = memo(function Card({ id, scope = 'board', maxDepth = 3, only, reorderable = false }: { id: string; scope?: string; maxDepth?: number; only?: Set<string>; reorderable?: boolean }) {
  const it = useItem(id)
  const cover = it?.coverId ? it.attachments.find((a) => a.id === it.coverId) : undefined
  const coverUrl = useFileUrl(cover?.thumbKey ?? cover?.key)
  // Drag a card by its header: onto the tab bar (pin as a tab) or the Pages tree.
  const drag = useDraggable({ id: `card:${id}`, data: { type: 'card', id } })
  const st = useTreeDnd()?.state
  const drop = useDroppable({ id: `zone:card:${id}`, data: { type: 'zone' }, disabled: !reorderable || st?.kind !== 'card' })
  const mark = drop.isOver && st?.activeId !== id ? st?.pageDrop : null
  // A row in the open beside it: it'll be a new card here.
  const newHere = st?.kind === 'row' && st.newCard?.target === id ? st.newCard.side : null
  // Its total: the one it's set to show (a detail of its own, SPEC §7.12), else the money inside
  // it added up (the fields the card shows under its rows).
  const auto = useSubtreeTotal(id)
  // In a tab, the card's options can set things just for that tab (its subtitle).
  const tab = useTab()
  if (!it) return null
  const set = it.total ? it.calc?.[it.total] : undefined
  const total = set ? (set.value ? set.text : '') : auto
  const title = displayTitle(it.title, (x) => store.get(x)?.title) || 'Untitled'
  return (
    <div ref={drop.setNodeRef} className="relative" data-drag={`card:${id}`} data-zone={`zone:card:${id}`}>
      <DropBar side={mark || newHere} label={newHere ? 'New card' : undefined} />
      {mark === 'inside' && <DropInside />}
      <section className={`tz-card ${tintClass(it.tint)} ${drag.isDragging ? 'opacity-40' : ''}`} data-card={id} aria-label={title}>
        <header ref={drag.setNodeRef} {...drag.listeners} {...drag.attributes} role="group" tabIndex={-1} className="card-bar flex touch-pan-y items-center gap-1.5 select-none">
          {it.system === 'inbox' && <IconInbox size={14} className="flex-none opacity-70" />}
          {it.system === 'agent-log' && <IconSparkle size={14} className="flex-none opacity-70" />}
          <CardTitle id={id} scope={scope} title={title} />
          {total && (
            <span className="lab flex-none tabular-nums opacity-70" title="Total of what's inside" data-card-total>
              {total}
            </span>
          )}
          {it.pinned && it.system !== 'inbox' && <IconPin size={12} className="flex-none opacity-60" />}
          <button
            type="button"
            onClick={() => openSheet('context', { id, ...(tab ? { view: tab.view } : {}) })}
            className="-mr-2 flex h-8 w-8 flex-none items-center justify-center opacity-70 hover:opacity-100"
            aria-label="Card options"
          >
            <IconMore size={16} />
          </button>
        </header>
        {coverUrl && (
          <button type="button" className="block w-full" onClick={() => navigate(itemPath(id))} aria-label={`Open ${title}`}>
            <img src={coverUrl} alt="" className="h-28 w-full object-cover" loading="lazy" />
          </button>
        )}
        <div className="card-body">
          <Checklist rootId={id} scope={scope} maxDepth={maxDepth} only={only} emptyHint={it.system === 'inbox' ? 'Nothing in the Inbox' : undefined} />
        </div>
      </section>
    </div>
  )
})
